import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = [
  { value: 'appartement', label: 'Appartement' },
  { value: 'maison', label: 'Maison' },
  { value: 'immeuble', label: 'Immeuble' },
  { value: 'local_commercial', label: 'Local commercial' },
  { value: 'terrain', label: 'Terrain' },
  { value: 'autre', label: 'Autre' },
];

const FISCAL_PROFILES = [
  { value: 'particulier', label: 'Particulier' },
  { value: 'sci_ir', label: 'SCI à l\'IR' },
  { value: 'sci_is', label: 'SCI à l\'IS' },
  { value: 'marchand_biens', label: 'Marchand de biens' },
];

export default function ProjectCompletionPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyDone, setAlreadyDone] = useState(false);
  const [clientName, setClientName] = useState('');
  const [forfait, setForfait] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    address: '',
    surface: '',
    purchase_price: '',
    property_type: 'appartement',
    fiscal_profile: 'particulier',
    land_surface: '',
    construction_year: '',
    dpe_class: '',
    description: '',
    estimated_work: '',
    sale_type: 'classique',
  });

  useEffect(() => {
    axios.get(`${API}/project/complete/${token}`)
      .then(r => {
        if (r.data.already_done) {
          setAlreadyDone(true);
          setClientName(r.data.client_name || '');
        } else {
          setClientName(r.data.client_name || '');
          setForfait(r.data.forfait || '');
        }
      })
      .catch(() => setError('Lien invalide ou expiré.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.address) { setError('Veuillez renseigner l\'adresse du bien.'); return; }
    if (!form.surface || parseFloat(form.surface) <= 0) { setError('Veuillez renseigner la surface.'); return; }
    if (!form.purchase_price || parseFloat(form.purchase_price) <= 0) { setError('Veuillez renseigner le prix d\'achat.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/project/complete/${token}`, {
        ...form,
        surface: parseFloat(form.surface) || 0,
        purchase_price: parseFloat(form.purchase_price) || 0,
        land_surface: form.land_surface ? parseFloat(form.land_surface) : null,
        construction_year: form.construction_year ? parseInt(form.construction_year) : null,
        estimated_work: form.estimated_work ? parseFloat(form.estimated_work) : null,
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Une erreur est survenue.');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  if (loading) return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error && !form.address) return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-4">
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-md text-center">
        <p className="text-red-600 text-lg font-semibold">{error}</p>
        <p className="text-gray-500 mt-2 text-sm">Ce lien est peut-être invalide ou a déjà été utilisé.</p>
      </div>
    </div>
  );

  if (alreadyDone) return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Dossier déjà complété</h2>
        <p className="text-gray-600">Bonjour {clientName}, votre dossier a déjà été complété. Notre expert travaille dessus.</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Merci {clientName} !</h2>
        <p className="text-gray-600 mb-4">Votre dossier est complet. Notre expert va prendre en charge votre audit et vous recontacter très prochainement.</p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          Pensez à garder votre email à portée de main pour recevoir votre rapport.
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A192F] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block bg-amber-400 text-[#0A192F] text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wider">
            Étude {forfait} — Offerte
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Bonjour {clientName},</h1>
          <p className="text-gray-400">Renseignez les informations de votre bien pour que notre expert puisse réaliser votre audit.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Bien */}
          <div className="p-6 border-b border-gray-100">
            <h2 className="font-bold text-[#0A192F] mb-4 flex items-center gap-2">
              <span className="w-6 h-6 bg-[#0A192F] text-white rounded-full text-xs flex items-center justify-center">1</span>
              Le bien immobilier
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse complète *</label>
                <input value={form.address} onChange={e => set('address', e.target.value)} placeholder="15 rue de la Paix, 75001 Paris" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de bien *</label>
                  <select value={form.property_type} onChange={e => set('property_type', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400">
                    {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Surface habitable (m²) *</label>
                  <input type="number" value={form.surface} onChange={e => set('surface', e.target.value)} placeholder="75" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" required />
                </div>
              </div>
              {(form.property_type === 'maison' || form.property_type === 'terrain') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Surface terrain (m²)</label>
                  <input type="number" value={form.land_surface} onChange={e => set('land_surface', e.target.value)} placeholder="200" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Année de construction</label>
                  <input type="number" value={form.construction_year} onChange={e => set('construction_year', e.target.value)} placeholder="1985" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Classe DPE</label>
                  <select value={form.dpe_class} onChange={e => set('dpe_class', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400">
                    <option value="">Je ne sais pas</option>
                    {['A','B','C','D','E','F','G'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Finances */}
          <div className="p-6 border-b border-gray-100">
            <h2 className="font-bold text-[#0A192F] mb-4 flex items-center gap-2">
              <span className="w-6 h-6 bg-[#0A192F] text-white rounded-full text-xs flex items-center justify-center">2</span>
              Informations financières
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix d'achat (€) *</label>
                  <input type="number" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} placeholder="250000" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget travaux estimé (€)</label>
                  <input type="number" value={form.estimated_work} onChange={e => set('estimated_work', e.target.value)} placeholder="30000" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Profil fiscal</label>
                  <select value={form.fiscal_profile} onChange={e => set('fiscal_profile', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400">
                    {FISCAL_PROFILES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de vente</label>
                  <select value={form.sale_type} onChange={e => set('sale_type', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400">
                    <option value="classique">Classique</option>
                    <option value="marchand">Marchand de biens</option>
                    <option value="encheres">Enchères</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Commentaires */}
          <div className="p-6 border-b border-gray-100">
            <h2 className="font-bold text-[#0A192F] mb-4 flex items-center gap-2">
              <span className="w-6 h-6 bg-[#0A192F] text-white rounded-full text-xs flex items-center justify-center">3</span>
              Informations complémentaires
            </h2>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Points spécifiques à analyser, travaux connus, contexte particulier..." className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 resize-none" />
          </div>

          {/* Submit */}
          <div className="p-6 bg-gray-50">
            {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
            <button type="submit" disabled={submitting} className="w-full bg-[#0A192F] hover:bg-[#1a2f52] text-white font-bold py-3.5 rounded-xl transition-colors disabled:opacity-60">
              {submitting ? 'Envoi en cours...' : 'Envoyer mon dossier'}
            </button>
            <p className="text-xs text-gray-400 text-center mt-3">Vos informations sont transmises en toute sécurité à notre expert.</p>
          </div>
        </form>
      </div>
    </div>
  );
}
